<template>
  <div class="container mt-2">
    <div class="d-flex flex-wrap align-items-center justify-content-between">
      <empresa-combo :selecionar="changeEmpresa" :noStyle="false" />
      <router-link class="btn btn-outline-primary" to="/configuracoes/impressoras/monitor">
        <i class="fa fa-heartbeat mr-1"></i> Monitor ao vivo
      </router-link>
    </div>

    <token-app-impressao :empresa="empresa" />

    <b-alert show variant="info" class="text-left small">
      <b>Criar e excluir impressora</b> continua no app de impressão de cada computador (é lá que se
      escolhe a impressora do Windows). Aqui você define <b>o que cada impressora imprime</b>.
      <br />
      O app de impressão aplica o que for salvo aqui em até 30 segundos a partir da próxima
      atualização dele. Até lá, só <b>Impressora ativa</b>, <b>Caixas</b>, <b>Mesas - Conta</b> e
      <b>Confirm. Saída</b> valem na hora, e salvar a impressora no próprio app sobrescreve o que foi
      alterado aqui.
    </b-alert>

    <div v-if="loading" class="text-center my-4">
      <b-spinner small grow variant="primary" /> carregando...
    </div>
    <div v-else-if="!computadores.length" class="text-muted my-4">
      Nenhuma impressora cadastrada. Cadastre pelo app de impressão no computador.
    </div>

    <div v-for="pc in computadores" :key="pc.computador" class="pc mb-3 text-left">
      <div class="pc-titulo">
        <i class="fa fa-desktop mr-2"></i>{{ pc.computador || "Sem computador" }}
        <b-badge v-if="pc.antigo" variant="warning" class="ml-2">
          sem uso há mais de {{ DIAS_SEM_USO }} dias — computador antigo?
        </b-badge>
      </div>
      <div
        v-for="imp in pc.impressoras"
        :key="imp.cod_impressora"
        class="impressora d-flex flex-wrap align-items-center"
        :class="{ inativa: !imp.ativo }"
      >
        <div class="flex-grow-1 pr-2">
          <div class="nome">
            <i class="fa fa-print mr-2"></i>{{ imp.nome }}
            <b-badge :variant="imp.ativo ? 'success' : 'secondary'" class="ml-2">
              {{ imp.ativo ? "Ativa" : "Inativa" }}
            </b-badge>
            <b-badge v-if="imp.ativo && semUso(imp)" variant="warning" class="ml-1">possível cadastro antigo</b-badge>
          </div>
          <div class="detalhe">{{ resumo(imp, nomesCaixas(imp.caixas)) }}</div>
          <div class="detalhe" v-for="(a, i) in avisos(imp)" :key="i">
            <span class="text-danger"><i class="fa fa-exclamation-triangle mr-1"></i>{{ a }}</span>
          </div>
          <div class="detalhe">
            {{ imp.caminho }} · {{ imp.colunas }} colunas
            <template v-if="config.comUltimoUso">
              · última impressão: <b>{{ quando(imp.ultima_impressao) }}</b>
              · última atividade: {{ quando(imp.ultima_atividade) }}
            </template>
          </div>
        </div>
        <button class="btn btn-sm btn-outline-secondary" @click="editar(imp)">
          <i class="fa fa-edit"></i> Editar
        </button>
      </div>
    </div>

    <div v-if="!loading && computadores.length && !config.comUltimoUso" class="small text-muted text-left mb-3">
      Para ver a última vez que cada impressora foi usada, rode as migrations da API (<code>node ace migration:run</code>).
    </div>

    <b-modal
      id="modal-impressora"
      v-model="modal"
      size="lg"
      title="Manutenção de Impressora"
      :busy="salvando"
      ok-title="Salvar Impressora"
      cancel-title="Cancelar"
      @ok.prevent="salvar"
    >
      <div v-if="form" class="text-left form-impressora">
        <div class="row">
          <div class="col-md-6">
            <label>Nome</label>
            <b-input v-model="form.nome" />
          </div>
          <div class="col-md-6">
            <label>Impressora do Windows</label>
            <b-input :value="form.caminho" disabled />
          </div>
          <div class="col-md-6 mt-2">
            <label>Computador / Identificador</label>
            <b-input :value="`${form.computador} · ${form.identificador}`" disabled />
          </div>
          <div class="col-md-6 mt-2">
            <label>Status</label>
            <b-check v-model="form.ativo" switch>Impressora Ativa</b-check>
            <small class="text-muted d-block">Inativa não recebe nada da fila.</small>
          </div>
        </div>

        <b-alert v-for="(a, i) in avisos(form)" :key="i" show variant="warning" class="small mt-3 mb-0 py-2">
          <i class="fa fa-exclamation-triangle mr-1"></i>{{ a }}
        </b-alert>

        <div class="grupo mt-3">
          <div class="grupo-titulo">O que esta impressora imprime</div>
          <div v-for="campo in tiposImpressao" :key="campo" class="opcao">
            <b-check v-model="form.tipo[campo]">
              <b>{{ ajuda[campo].titulo }}</b>
              <ajuda-impressao :ajuda="ajuda[campo]" />
              <span class="opcao-desc">{{ ajuda[campo].resumo }}</span>
            </b-check>
            <div v-if="campo === 'senhas' && form.tipo.senhas" class="sub-opcao">
              <label class="mb-0 mr-2">
                Tipo de senha <ajuda-impressao :ajuda="ajuda.tipoSenha" />
              </label>
              <b-select v-model="form.tipoSenha" :options="config.tiposSenha" size="sm" style="max-width: 200px" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <div class="grupo mt-3">
              <div class="grupo-titulo d-flex justify-content-between">
                <span>Tipo de Entrega <ajuda-impressao :ajuda="ajuda.tiposEntrega" /></span>
                <a href="#" @click.prevent="form.tiposEntrega = [...config.tiposEntrega]">marcar todos</a>
              </div>
              <div class="opcao-desc mb-1">{{ ajuda.tiposEntrega.resumo }}</div>
              <b-check-group v-model="form.tiposEntrega" :options="config.tiposEntrega" stacked />
            </div>
          </div>
          <div class="col-md-8">
            <div class="grupo mt-3">
              <div class="grupo-titulo d-flex justify-content-between">
                <span>Caixas <ajuda-impressao :ajuda="ajuda.caixasLista" /></span>
                <a href="#" @click.prevent="form.caixas = config.caixas.map((c) => c.cod_caixa)">marcar todos</a>
              </div>
              <div class="opcao-desc mb-1">{{ ajuda.caixasLista.resumo }}</div>
              <b-check-group
                v-model="form.caixas"
                :options="config.caixas.map((c) => ({ value: c.cod_caixa, text: `${c.cod_caixa} - ${c.nome}` }))"
                stacked
              />
            </div>
          </div>
        </div>

        <div class="grupo mt-3">
          <div class="grupo-titulo d-flex justify-content-between">
            <span>Categorias / Grupo de Produto <ajuda-impressao :ajuda="ajuda.grupos" /></span>
            <a href="#" @click.prevent="inverterGrupos">inverter seleção</a>
          </div>
          <div class="opcao-desc mb-1">{{ ajuda.grupos.resumo }}</div>
          <b-check-group
            v-model="form.grupoProdutos"
            :options="config.grupos.map((g) => ({ value: g.cod_grupo, text: `${g.cod_grupo} - ${g.Grupo}` }))"
            class="lista-grupos"
          />
        </div>

        <div class="grupo mt-3">
          <div class="grupo-titulo">Parâmetros</div>
          <div class="d-flex flex-wrap align-items-center mb-2">
            <label class="mr-2 mb-0">Colunas <ajuda-impressao :ajuda="ajuda.colunas" /></label>
            <b-input v-model.number="form.colunas" type="number" min="1" max="200" style="width: 90px" class="mr-2" />
            <span class="opcao-desc">{{ ajuda.colunas.resumo }}</span>
          </div>
          <b-check v-model="form.tipo.exibe_taxa_entrega">
            <b>{{ ajuda.exibe_taxa_entrega.titulo }}</b>
            <ajuda-impressao :ajuda="ajuda.exibe_taxa_entrega" />
            <span class="opcao-desc">{{ ajuda.exibe_taxa_entrega.resumo }}</span>
          </b-check>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import EmpresaCombo from "../../components/empresa/EmpresaCombo.vue";
import AjudaImpressao from "../../components/impressoras/AjudaImpressao.vue";
import TokenAppImpressao from "../../components/impressoras/TokenAppImpressao.vue";
import ImpressorasConfigLib from "@/libs/ImpressorasConfigLib";
import { AJUDA, TIPOS_IMPRESSAO, avisos, resumo } from "./impressorasAjuda";

// Sem imprimir ha mais que isso = provavel cadastro antigo (PC trocado, impressora parada).
const DIAS_SEM_USO = 7;

export default {
  components: { EmpresaCombo, AjudaImpressao, TokenAppImpressao },
  data() {
    return {
      empresa: null,
      loading: true,
      config: { impressoras: [], caixas: [], grupos: [], tiposEntrega: [], tiposSenha: [], comUltimoUso: false },
      ajuda: AJUDA,
      tiposImpressao: TIPOS_IMPRESSAO,
      DIAS_SEM_USO,
      modal: false,
      form: null,
      salvando: false,
    };
  },
  computed: {
    computadores() {
      const pcs = {};
      for (const imp of this.config.impressoras) {
        const pc = imp.computador || "";
        if (!pcs[pc]) pcs[pc] = { computador: pc, impressoras: [] };
        pcs[pc].impressoras.push(imp);
      }
      for (const pc of Object.values(pcs)) {
        pc.antigo = this.config.comUltimoUso && pc.impressoras.every((i) => this.semUso(i));
      }
      // PCs em uso com impressora ativa primeiro; antigos por ultimo.
      return Object.values(pcs).sort(
        (a, b) =>
          a.antigo - b.antigo ||
          b.impressoras.some((i) => i.ativo) - a.impressoras.some((i) => i.ativo) ||
          a.computador.localeCompare(b.computador)
      );
    },
  },
  methods: {
    avisos,
    resumo,
    changeEmpresa(emp) {
      this.empresa = emp;
      this.carregar();
    },
    async carregar() {
      if (!this.empresa) return;
      this.loading = true;
      try {
        const res = await ImpressorasConfigLib.get(this.empresa.cod_empresa);
        if (res.success) this.config = res.data;
        else this.showToast("error", res.message || "Erro ao carregar impressoras");
      } catch (e) {
        this.showToast("error", "Erro ao carregar impressoras");
      }
      this.loading = false;
    },
    // A atividade vem do banco desde a instalacao do script; a impressao real, do exe novo.
    semUso(imp) {
      if (!this.config.comUltimoUso) return false;
      const ultima = [imp.ultima_impressao, imp.ultima_atividade].filter(Boolean).sort().pop();
      if (!ultima) return true;
      return this.$moment(this.config.agora).diff(this.$moment(ultima), "days") >= DIAS_SEM_USO;
    },
    quando(data) {
      if (!data) return "sem registro";
      return `${this.$moment(data).from(this.$moment(this.config.agora))} (${this.$moment(data).format("DD/MM HH:mm")})`;
    },
    nomesCaixas(caixas) {
      if (!caixas || !caixas.length) return "todos";
      return caixas
        .map((c) => {
          const cx = this.config.caixas.find((x) => x.cod_caixa == c);
          return cx ? cx.nome : c;
        })
        .join(", ");
    },
    editar(imp) {
      this.form = JSON.parse(JSON.stringify(imp));
      if (!this.form.tipoSenha) this.form.tipoSenha = "Somente Senha";
      this.modal = true;
    },
    inverterGrupos() {
      this.form.grupoProdutos = this.config.grupos
        .map((g) => g.cod_grupo)
        .filter((c) => !this.form.grupoProdutos.includes(c));
    },
    async salvar() {
      this.salvando = true;
      try {
        const res = await ImpressorasConfigLib.salvar(this.empresa.cod_empresa, this.form);
        if (res.success) {
          const idx = this.config.impressoras.findIndex((i) => i.cod_impressora === res.data.cod_impressora);
          if (idx >= 0) this.$set(this.config.impressoras, idx, res.data);
          this.modal = false;
          this.showToast("success", "Impressora salva!");
        } else {
          this.showToast("error", res.message || "Erro ao salvar");
        }
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        this.showToast("error", msg || "Erro ao salvar");
      }
      this.salvando = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.pc {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 10px;
  .pc-titulo {
    font-weight: bold;
    margin-bottom: 6px;
  }
}
.impressora {
  background: white;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 6px;
  .nome {
    font-size: 1rem;
  }
  .detalhe {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
  }
  &.inativa {
    opacity: 0.6;
  }
}
.form-impressora {
  label {
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 2px;
  }
  .opcao-desc {
    display: block;
    font-size: 12px;
    color: #666;
    font-weight: normal;
  }
  .opcao {
    padding: 4px 0;
    border-bottom: 1px solid #f2f2f2;
    &:last-child {
      border-bottom: none;
    }
  }
  .sub-opcao {
    display: flex;
    align-items: center;
    margin: 4px 0 2px 24px;
  }
  .grupo {
    border: 1px solid #e3e3e3;
    border-radius: 4px;
    padding: 8px 10px;
    .grupo-titulo {
      font-weight: bold;
      font-size: 13px;
      margin-bottom: 6px;
      a {
        font-weight: normal;
        font-size: 12px;
      }
    }
  }
  .lista-grupos {
    column-count: 3;
    ::v-deep .custom-control {
      display: block;
    }
  }
}
@media (max-width: 768px) {
  .form-impressora .lista-grupos {
    column-count: 1;
  }
}
</style>
